import axios from 'axios';
import { encryptFilters, decryptFilters } from './encryptionService';

const API_BASE_URL = process.env.REACT_APP_SAVED_FILTERS_API_URL;

// Get user data
const getAuthData = () => {
  try {
    const allKeys = Object.keys(localStorage);

    const lastAuthUserKey = allKeys.find(
      k => k.includes('CognitoIdentityServiceProvider') && k.includes('LastAuthUser'),
    );
    if (!lastAuthUserKey) return { token: null, userId: null };

    const parts = lastAuthUserKey.split('.');
    const clientIdIndex = parts.findIndex(p => p === 'CognitoIdentityServiceProvider') + 1;
    const clientId = parts[clientIdIndex];
    const userSub = localStorage.getItem(lastAuthUserKey);

    if (!clientId || !userSub) return { token: null, userId: null };

    const possibleBaseKeys = [
      `CognitoIdentityServiceProvider.${clientId}.${userSub}`,
      `CognitoIdentityServiceProvider.${clientId}.LastAuthUser.${userSub}`,
    ];

    let idToken = null as string | null;
    let accessToken = null as string | null;

    for (const keyBase of possibleBaseKeys) {
      const id = localStorage.getItem(`${keyBase}.idToken`);
      const acc = localStorage.getItem(`${keyBase}.accessToken`);
      if (id || acc) {
        idToken = id;
        accessToken = acc;
        break;
      }
    }

    if (!idToken && !accessToken) {
      console.warn('No idToken or accessToken found for clientId:', clientId);
      return { token: null, userId: null };
    }

    const token = idToken || accessToken;
    const payload = parseJwt(token);
    const userId = payload?.sub || userSub || null;

    return { token, userId };
  } catch (e) {
    console.error('Error extracting Cognito tokens:', e);
    return { token: null, userId: null };
  }
};

// Parse user token
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const getAuthToken = () => {
  return getAuthData().token;
};

// Create api URL call
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  config => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error),
);

/**
 * Save filters
 * @param {string} name
 * @param {Array} filters
 * @param {boolean} isEvidence
 * @param {string} searchQuery
 */
export const saveFilter = async (name, filters, isEvidence = false, searchQuery = null) => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const encryptedFilters = filters ? encryptFilters(filters, userId) : [];

    const payload = {
      name,
      filters: encryptedFilters,
      isEvidence,
      searchQuery,
    };

    if (searchQuery) {
      payload.searchQuery = searchQuery;
    }

    const response = await apiClient.post('/filters', payload);

    if (response.data.filter && response.data.filter.filters) {
      response.data.filter.filters = decryptFilters(response.data.filter.filters, userId);
    }

    return response.data;
  } catch (error) {
    console.error('Error saving filter:', error);
    throw error;
  }
};

/**
 * Save search query without filters
 * @param {string} name
 * @param {string} searchQuery
 * @param {boolean} isEvidence
 */
export const saveSearchQuery = async (name, searchQuery, isEvidence = false) => {
  return saveFilter(name, [], isEvidence, searchQuery);
};

// Get filters
export const getFilters = async () => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const response = await apiClient.get('/filters');

    if (response.data.filters && Array.isArray(response.data.filters)) {
      response.data.filters = response.data.filters.map(filter => {
        return {
          ...filter,
          filters: decryptFilters(filter.filters, userId),
          searchQuery: filter.searchQuery,
        };
      });
    }

    return response.data;
  } catch (error) {
    console.error('Error getting filters:', error);
    throw error;
  }
};

/**
 * Remove filter
 * @param {string} filterId
 */
export const deleteFilter = async filterId => {
  try {
    const response = await apiClient.delete(`/filters/${filterId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting filter:', error);
    throw error;
  }
};

/**
 * Update filter
 * @param {string} filterId
 * @param {Object} updates
 */
export const updateFilter = async (filterId, updates) => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const payload = { ...updates };

    if (updates.filters) {
      payload.filters = encryptFilters(updates.filters, userId);
    }

    const response = await apiClient.put(`/filters/${filterId}`, payload);

    if (response.data.filter && response.data.filter.filters) {
      response.data.filter.filters = decryptFilters(response.data.filter.filters, userId);
    }

    return response.data;
  } catch (error) {
    console.error('Error updating filter:', error);
    throw error;
  }
};

// Filters from LS to cloud
export const migrateLocalFiltersToCloud = async () => {
  try {
    const { userId } = getAuthData();

    if (!userId) {
      throw new Error('User not authenticated');
    }

    const localFilters = localStorage.getItem('filters');
    if (!localFilters) return { migrated: 0 };

    const filters = JSON.parse(localFilters);
    const migrationPromises = filters.map((filterGroup, index) =>
      saveFilter(
        `Migrated Filter ${index + 1}`,
        Array.isArray(filterGroup) ? filterGroup : [filterGroup],
        filterGroup.isEvidence || false,
        null,
      ),
    );

    await Promise.all(migrationPromises);

    localStorage.removeItem('filters');

    return { migrated: filters.length };
  } catch (error) {
    console.error('Error migrating filters:', error);
    throw error;
  }
};
